import { useEffect, useState } from "react";
import api, { errMsg, formatTL, formatNumber, formatDate, API } from "@/lib/api";
import PageHeader from "@/components/app/PageHeader";
import KPICard from "@/components/app/KPICard";
import { Plus, X, Trash2, Pencil, Download, Upload, Wand2, FileSpreadsheet, TrendingUp, BarChart3, Copy, FileText, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Cell, Legend
} from "recharts";

const emptyLine = () => ({
  guzergah: "", arac_kapasitesi: "16+1", calisma_saatleri: "",
  calisma_gun_sayisi: 22, gunluk_sefer_sayisi: 2,
  tek_yon_mesafe_km: 0, tek_yon_fiyat: 0, gunluk_fiyat: 0, aylik_fiyat: 0,
});
const emptyOffer = () => ({
  offer_no: "", offer_date: new Date().toISOString().slice(0,10),
  company_name: "", authorized_person: "", contact: "",
  prepared_by: "", prepared_phone: "", prepared_email: "",
  status: "draft", notes: "",
  line_items: [emptyLine()],
});

const STATUSES = [
  { value: "draft", label: "Taslak" },
  { value: "sent", label: "Gönderildi" },
  { value: "won", label: "Kazanıldı" },
  { value: "lost", label: "Kaybedildi" },
];
const statusColor = { draft: "bg-neutral-400", sent: "bg-[#0020C2]", won: "bg-[#16A34A]", lost: "bg-[#DC2626]" };

export default function OffersPage() {
  const [tab, setTab] = useState("list"); // list | new | analytics | ai
  const [offers, setOffers] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyOffer());
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const [o, a] = await Promise.all([
        api.get("/offers"),
        api.get("/offers/analytics/summary"),
      ]);
      setOffers(o.data);
      setAnalytics(a.data);
    } catch (e) { toast.error(errMsg(e)); }
  };
  useEffect(() => { load(); }, []);

  // Auto-recompute totals as user types
  const recompute = (li) => {
    const tf = Number(li.tek_yon_fiyat) || 0;
    const gs = Number(li.gunluk_sefer_sayisi) || 0;
    const gd = Number(li.calisma_gun_sayisi) || 0;
    const gunluk = tf * gs;
    const aylik = gunluk * gd;
    return { ...li, gunluk_fiyat: gunluk, aylik_fiyat: aylik };
  };

  const updateLine = (idx, patch) => {
    const items = [...form.line_items];
    items[idx] = recompute({ ...items[idx], ...patch });
    setForm({ ...form, line_items: items });
  };
  const addLine = () => setForm({ ...form, line_items: [...form.line_items, emptyLine()] });
  const removeLine = (idx) => setForm({ ...form, line_items: form.line_items.filter((_, i) => i !== idx) });

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        line_items: form.line_items.map(li => ({
          ...li,
          calisma_gun_sayisi: Number(li.calisma_gun_sayisi) || 0,
          gunluk_sefer_sayisi: Number(li.gunluk_sefer_sayisi) || 0,
          tek_yon_mesafe_km: Number(li.tek_yon_mesafe_km) || 0,
          tek_yon_fiyat: Number(li.tek_yon_fiyat) || 0,
        })),
      };
      if (editing) await api.put(`/offers/${editing.id}`, payload);
      else await api.post("/offers", payload);
      toast.success("Teklif kaydedildi");
      setForm(emptyOffer());
      setEditing(null);
      setTab("list");
      load();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  const editOffer = (o) => {
    setEditing(o);
    setForm({
      offer_no: o.offer_no || "",
      offer_date: o.offer_date || "",
      company_name: o.company_name || "",
      authorized_person: o.authorized_person || "",
      contact: o.contact || "",
      prepared_by: o.prepared_by || "",
      prepared_phone: o.prepared_phone || "",
      prepared_email: o.prepared_email || "",
      status: o.status || "draft",
      notes: o.notes || "",
      line_items: (o.line_items || []).map(li => ({ ...li })),
    });
    setTab("new");
  };
  const duplicateOffer = (o) => {
    setEditing(null);
    setForm({
      offer_no: `${o.offer_no || ""}-KOPYA`,
      offer_date: new Date().toISOString().slice(0,10),
      company_name: o.company_name || "",
      authorized_person: o.authorized_person || "",
      contact: o.contact || "",
      prepared_by: o.prepared_by || "",
      prepared_phone: o.prepared_phone || "",
      prepared_email: o.prepared_email || "",
      status: "draft",
      notes: o.notes || "",
      line_items: (o.line_items || []).map(li => ({ ...li })),
    });
    setTab("new");
    toast.info("Teklif kopyalandı — düzenleyerek kaydedin");
  };

  const removeOffer = async (o) => {
    if (!window.confirm("Teklifi silmek istediğinize emin misiniz?")) return;
    try { await api.delete(`/offers/${o.id}`); toast.success("Silindi"); load(); }
    catch (err) { toast.error(errMsg(err)); }
  };

  const downloadXlsx = (o) => {
    const token = localStorage.getItem("token");
    window.open(`${API}/offers/${o.id}/xlsx?auth=${token || ""}`, "_blank");
  };
  const downloadTemplate = () => {
    const token = localStorage.getItem("token");
    window.open(`${API}/offers/template/blank?auth=${token || ""}`, "_blank");
  };

  const uploadXlsx = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/offers/upload-xlsx", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setEditing(null);
      setForm({
        ...emptyOffer(),
        ...data.header,
        offer_date: data.header.offer_date || new Date().toISOString().slice(0,10),
        line_items: data.line_items.length ? data.line_items : [emptyLine()],
      });
      setTab("new");
      toast.success(`Excel içe aktarıldı — ${data.line_items.length} güzergah bulundu. Düzenleyerek kaydedin.`);
    } catch (err) { toast.error(errMsg(err)); }
  };

  // AI suggest state
  const [aiRoutes, setAiRoutes] = useState([{ guzergah: "", arac_kapasitesi: "16+1", tek_yon_mesafe_km: 0, gunluk_sefer_sayisi: 2, calisma_gun_sayisi: 22, calisma_saatleri: "" }]);
  const [aiResult, setAiResult] = useState(null);
  const runSuggest = async () => {
    try {
      const { data } = await api.post("/offers/suggest", { routes: aiRoutes });
      setAiResult(data);
      toast.success(`${data.suggestions.length} güzergah için otomatik teklif oluşturuldu`);
    } catch (err) { toast.error(errMsg(err)); }
  };
  const applyAISuggestions = () => {
    if (!aiResult) return;
    setEditing(null);
    setForm({
      ...emptyOffer(),
      offer_date: new Date().toISOString().slice(0,10),
      line_items: aiResult.suggestions.map(s => ({
        guzergah: s.guzergah, arac_kapasitesi: s.arac_kapasitesi, calisma_saatleri: s.calisma_saatleri || "",
        calisma_gun_sayisi: s.calisma_gun_sayisi, gunluk_sefer_sayisi: s.gunluk_sefer_sayisi,
        tek_yon_mesafe_km: s.tek_yon_mesafe_km, tek_yon_fiyat: s.tek_yon_fiyat,
        gunluk_fiyat: s.gunluk_fiyat, aylik_fiyat: s.aylik_fiyat,
      })),
    });
    setTab("new");
    toast.success("Otomatik teklif düzenleme formuna aktarıldı");
  };

  const formTotals = form.line_items.reduce((acc, li) => {
    acc.tek_yon += Number(li.tek_yon_fiyat) || 0;
    acc.gunluk += Number(li.gunluk_fiyat) || 0;
    acc.aylik += Number(li.aylik_fiyat) || 0;
    return acc;
  }, { tek_yon: 0, gunluk: 0, aylik: 0 });

  return (
    <>
      <PageHeader
        title="Teklifler"
        subtitle="Firmalara güzergah bazlı teklif hazırlama, arşivleme, Excel içe/dışa aktarma ve geçmiş verilerle otomatik fiyatlama."
        testid="offers-header"
        actions={
          <div className="flex items-center gap-2">
            <button data-testid="offers-download-template" onClick={downloadTemplate} className="flex items-center gap-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border border-[#E5E5E5] hover:bg-[#F5F5F5]">
              <Download className="w-3.5 h-3.5" /> Boş Şablon
            </button>
            <label className="flex items-center gap-2 px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border border-[#E5E5E5] hover:bg-[#F5F5F5] cursor-pointer">
              <Upload className="w-3.5 h-3.5" /> <span data-testid="offers-upload-xlsx">Excel İçe Aktar</span>
              <input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadXlsx(f); e.target.value = ""; }} />
            </label>
            <button
              data-testid="offers-new-btn"
              onClick={() => { setEditing(null); setForm(emptyOffer()); setTab("new"); }}
              className="flex items-center gap-2 bg-[#0A0A0A] text-white px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] hover:bg-[#0020C2] transition-colors"
            >
              <Plus className="w-4 h-4" strokeWidth={2.2} /> Yeni Teklif
            </button>
          </div>
        }
      />

      {/* Tabs */}
      <div className="flex gap-2 border-b border-[#E5E5E5] mb-6">
        {[
          { k: "list", label: "Tüm Teklifler", testid: "tab-list" },
          { k: "new", label: editing ? "Düzenle" : "Yeni Teklif", testid: "tab-new" },
          { k: "ai", label: "🤖 Otomatik Teklif", testid: "tab-ai" },
          { k: "analytics", label: "Analiz", testid: "tab-analytics" },
        ].map(t => (
          <button
            key={t.k}
            data-testid={t.testid}
            onClick={() => setTab(t.k)}
            className={`px-4 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] border-b-2 -mb-px transition-colors ${
              tab === t.k ? "border-[#0A0A0A] text-[#0A0A0A]" : "border-transparent text-neutral-500 hover:text-[#0A0A0A]"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "list" && (
        <OffersList offers={offers} onEdit={editOffer} onDelete={removeOffer} onDownload={downloadXlsx} onDuplicate={duplicateOffer} reload={load} />
      )}

      {tab === "new" && (
        <OfferForm
          form={form} setForm={setForm}
          updateLine={updateLine} addLine={addLine} removeLine={removeLine}
          formTotals={formTotals} save={save} busy={busy} editing={editing}
        />
      )}

      {tab === "ai" && (
        <AISuggest aiRoutes={aiRoutes} setAiRoutes={setAiRoutes} aiResult={aiResult} runSuggest={runSuggest} applyAISuggestions={applyAISuggestions} />
      )}

      {tab === "analytics" && (
        <Analytics analytics={analytics} />
      )}
    </>
  );
}

// ─── List ─────────────────────────────────────────────────────────────────
function OffersList({ offers, onEdit, onDelete, onDownload, onDuplicate, reload }) {
  const [filesModalFor, setFilesModalFor] = useState(null);

  const uploadPdf = async (offer, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/offers/${offer.id}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("PDF yüklendi");
      reload && reload();
    } catch (err) { toast.error(errMsg(err)); }
  };
  const deletePdf = async (offer, fileId) => {
    if (!window.confirm("PDF'i silmek istiyor musunuz?")) return;
    try {
      await api.delete(`/offers/${offer.id}/files/${fileId}`);
      toast.success("Silindi");
      reload && reload();
      // Refresh modal target too if open
      setFilesModalFor(prev => prev && prev.id === offer.id ? { ...prev, files: (prev.files || []).filter(f => f.file_id !== fileId) } : prev);
    } catch (err) { toast.error(errMsg(err)); }
  };
  const fileUrl = (offerId, fileId) => {
    const token = localStorage.getItem("token");
    return `${API}/offers/${offerId}/files/${fileId}?auth=${token || ""}`;
  };

  if (offers.length === 0) {
    return (
      <div className="bg-white border border-[#E5E5E5] p-16 text-center rounded-md">
        <FileSpreadsheet className="w-10 h-10 mx-auto text-neutral-300 mb-4" strokeWidth={1.2} />
        <div className="text-neutral-500 text-sm">Henüz teklif kaydı yok. &quot;Yeni Teklif&quot; veya &quot;Excel İçe Aktar&quot; ile başlayın.</div>
      </div>
    );
  }
  return (
    <div className="bg-white border border-[#E5E5E5] rounded-md overflow-hidden">
      <div className="overflow-x-auto data-table">
        <table className="w-full text-sm">
          <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
            <tr>
              {["Teklif No","Tarih","Firma","Güzergah","Aylık Toplam","Durum",""].map((h, i) => (
                <th key={h} className={`px-4 py-3 text-[11px] uppercase tracking-widest text-neutral-500 font-semibold ${i === 4 ? "text-right" : "text-left"}`}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {offers.map(o => {
              const st = STATUSES.find(s => s.value === o.status) || STATUSES[0];
              return (
                <tr key={o.id} className="border-b border-[#F5F5F5]" data-testid={`offer-row-${o.id}`}>
                  <td className="px-4 py-3"><span className="num font-semibold">{o.offer_no}</span></td>
                  <td className="px-4 py-3">{formatDate(o.offer_date)}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{o.company_name}</div>
                    <div className="text-xs text-neutral-500">{o.authorized_person}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-xs text-neutral-600">{(o.line_items || []).length} güzergah</div>
                    <div className="text-xs text-neutral-500 truncate max-w-[240px]">{(o.line_items || []).map(x => x.guzergah).filter(Boolean).slice(0, 2).join(" • ")}{(o.line_items || []).length > 2 ? " ..." : ""}</div>
                  </td>
                  <td className="px-4 py-3 text-right num font-semibold text-[#16A34A]">{formatTL(o.totals?.aylik_toplam || 0)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <span className={`w-2 h-2 rounded-full ${statusColor[o.status] || "bg-neutral-400"}`} />
                      <span className="text-[10px] uppercase tracking-widest font-semibold text-neutral-500">{st.label}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1 items-center">
                      {(o.files || []).length > 0 && (
                        <button data-testid={`offer-files-${o.id}`} onClick={() => setFilesModalFor(o)} className="p-1.5 hover:bg-[#F5F5F5] flex items-center gap-1 border border-[#E5E5E5] text-[10px] font-semibold text-[#DC2626]" title="Ekli PDF dosyaları">
                          <FileText className="w-3.5 h-3.5" /> {(o.files || []).length}
                        </button>
                      )}
                      <label className="p-1.5 hover:bg-[#F5F5F5] cursor-pointer" title="PDF yükle">
                        <Upload className="w-3.5 h-3.5" />
                        <input data-testid={`offer-pdf-upload-${o.id}`} type="file" accept=".pdf,application/pdf" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPdf(o, f); e.target.value = ""; }} />
                      </label>
                      <button data-testid={`offer-download-${o.id}`} onClick={() => onDownload(o)} className="p-1.5 hover:bg-[#F5F5F5]" title="Excel indir"><Download className="w-3.5 h-3.5" /></button>
                      <button data-testid={`offer-copy-${o.id}`} onClick={() => onDuplicate(o)} className="p-1.5 hover:bg-[#F5F5F5]" title="Kopyala"><Copy className="w-3.5 h-3.5" /></button>
                      <button data-testid={`offer-edit-${o.id}`} onClick={() => onEdit(o)} className="p-1.5 hover:bg-[#F5F5F5]"><Pencil className="w-3.5 h-3.5" /></button>
                      <button data-testid={`offer-delete-${o.id}`} onClick={() => onDelete(o)} className="p-1.5 hover:bg-[#DC2626] hover:text-white"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filesModalFor && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setFilesModalFor(null)}>
          <div className="bg-white w-full max-w-2xl rounded-md shadow-lg" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-4 border-b border-[#E5E5E5]">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Teklif Dosyaları — {filesModalFor.offer_no}</div>
                <div className="font-heading font-bold text-lg">{filesModalFor.company_name}</div>
              </div>
              <button onClick={() => setFilesModalFor(null)} className="p-1.5 hover:bg-[#F5F5F5]"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-6">
              {(filesModalFor.files || []).length === 0 ? (
                <div className="text-sm text-neutral-500 text-center py-6">Henüz PDF eklenmemiş.</div>
              ) : (
                <div className="space-y-2">
                  {filesModalFor.files.map(f => (
                    <div key={f.file_id} className="flex items-center justify-between bg-[#FAFAFA] px-3 py-2 text-sm border border-[#F5F5F5]">
                      <div className="flex items-center gap-2 min-w-0">
                        <FileText className="w-4 h-4 text-neutral-500 shrink-0" />
                        <span className="truncate">{f.original_filename}</span>
                        <span className="text-[10px] text-neutral-400 whitespace-nowrap">{Math.round((f.size || 0)/1024)} KB</span>
                      </div>
                      <div className="flex items-center gap-1 shrink-0">
                        <a href={fileUrl(filesModalFor.id, f.file_id)} target="_blank" rel="noreferrer" className="p-1.5 hover:bg-white border border-[#E5E5E5]" title="Aç"><ExternalLink className="w-3.5 h-3.5" /></a>
                        <button onClick={() => deletePdf(filesModalFor, f.file_id)} className="p-1.5 hover:bg-[#DC2626] hover:text-white border border-[#E5E5E5]"><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <label className="mt-4 flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-[#E5E5E5] cursor-pointer hover:bg-[#FAFAFA] text-xs font-semibold uppercase tracking-widest">
                <Upload className="w-4 h-4" /> Yeni PDF Ekle
                <input type="file" accept=".pdf,application/pdf" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadPdf(filesModalFor, f); e.target.value = ""; }} />
              </label>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
function OfferForm({ form, setForm, updateLine, addLine, removeLine, formTotals, save, busy, editing }) {
  return (
    <div className="space-y-6" data-testid="offer-form">
      {/* Header */}
      <div className="bg-white border border-[#E5E5E5] rounded-md p-6">
        <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 font-semibold mb-4">Teklif Bilgileri</div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Field label="Teklif No *" testid="offer-field-no"><input required value={form.offer_no} onChange={e => setForm({...form, offer_no: e.target.value})} className="input" /></Field>
          <Field label="Teklif Tarihi *" testid="offer-field-date"><input type="date" required value={form.offer_date} onChange={e => setForm({...form, offer_date: e.target.value})} className="input num" /></Field>
          <Field label="Durum" testid="offer-field-status"><select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="input">{STATUSES.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}</select></Field>
          <Field label="Firma *" testid="offer-field-company"><input required value={form.company_name} onChange={e => setForm({...form, company_name: e.target.value})} className="input" /></Field>
          <Field label="Yetkili Kişi" testid="offer-field-authorized"><input value={form.authorized_person} onChange={e => setForm({...form, authorized_person: e.target.value})} className="input" /></Field>
          <Field label="İletişim" testid="offer-field-contact"><input value={form.contact} onChange={e => setForm({...form, contact: e.target.value})} className="input" /></Field>
          <Field label="Teklifi Hazırlayan"><input value={form.prepared_by} onChange={e => setForm({...form, prepared_by: e.target.value})} className="input" /></Field>
          <Field label="Telefon"><input value={form.prepared_phone} onChange={e => setForm({...form, prepared_phone: e.target.value})} className="input" /></Field>
          <Field label="E-posta"><input value={form.prepared_email} onChange={e => setForm({...form, prepared_email: e.target.value})} className="input" /></Field>
        </div>
      </div>

      {/* Line items */}
      <div className="bg-white border border-[#E5E5E5] rounded-md">
        <div className="px-6 py-4 border-b border-[#E5E5E5] flex items-center justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 font-semibold">Güzergah Kalemleri</div>
            <div className="text-lg font-bold font-heading mt-1">Personel Taşımacılığı Detayları</div>
          </div>
          <button data-testid="offer-add-line" onClick={addLine} className="flex items-center gap-2 px-3 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]">
            <Plus className="w-3.5 h-3.5" /> Güzergah Ekle
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
              <tr>
                {["Sıra","Güzergah","Kapasite","Saatler","Gün Sayısı","Sefer/Gün","Mesafe (Km)","Tek Yön ₺","Günlük ₺","Aylık ₺",""].map((h, i) => (
                  <th key={h} className={`px-2 py-3 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold ${i >= 6 ? "text-right" : "text-left"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {form.line_items.map((li, idx) => (
                <tr key={idx} className="border-b border-[#F5F5F5]" data-testid={`offer-line-${idx}`}>
                  <td className="px-2 py-2 text-neutral-400 num">{idx + 1}</td>
                  <td className="px-2 py-2"><input data-testid={`line-guzergah-${idx}`} value={li.guzergah} onChange={e => updateLine(idx, { guzergah: e.target.value })} className="w-full px-2 py-1.5 bg-[#FAFAFA] border border-transparent focus:bg-white focus:border-[#E5E5E5] text-xs" /></td>
                  <td className="px-2 py-2"><input value={li.arac_kapasitesi} onChange={e => updateLine(idx, { arac_kapasitesi: e.target.value })} className="w-20 px-2 py-1.5 bg-[#FAFAFA] border border-transparent focus:bg-white focus:border-[#E5E5E5] text-xs" /></td>
                  <td className="px-2 py-2"><input value={li.calisma_saatleri} onChange={e => updateLine(idx, { calisma_saatleri: e.target.value })} className="w-24 px-2 py-1.5 bg-[#FAFAFA] border border-transparent focus:bg-white focus:border-[#E5E5E5] text-xs" /></td>
                  <td className="px-2 py-2"><input type="number" step="any" value={li.calisma_gun_sayisi} onChange={e => updateLine(idx, { calisma_gun_sayisi: e.target.value })} className="w-16 px-2 py-1.5 bg-[#FAFAFA] border border-transparent focus:bg-white focus:border-[#E5E5E5] text-xs num text-right" /></td>
                  <td className="px-2 py-2"><input type="number" step="any" value={li.gunluk_sefer_sayisi} onChange={e => updateLine(idx, { gunluk_sefer_sayisi: e.target.value })} className="w-16 px-2 py-1.5 bg-[#FAFAFA] border border-transparent focus:bg-white focus:border-[#E5E5E5] text-xs num text-right" /></td>
                  <td className="px-2 py-2 text-right"><input type="number" step="any" value={li.tek_yon_mesafe_km} onChange={e => updateLine(idx, { tek_yon_mesafe_km: e.target.value })} className="w-20 px-2 py-1.5 bg-[#FAFAFA] border border-transparent focus:bg-white focus:border-[#E5E5E5] text-xs num text-right" /></td>
                  <td className="px-2 py-2 text-right"><input data-testid={`line-price-${idx}`} type="number" step="any" value={li.tek_yon_fiyat} onChange={e => updateLine(idx, { tek_yon_fiyat: e.target.value })} className="w-24 px-2 py-1.5 bg-[#FAFAFA] border border-transparent focus:bg-white focus:border-[#E5E5E5] text-xs num text-right" /></td>
                  <td className="px-2 py-2 text-right num text-neutral-600">{formatTL(li.gunluk_fiyat)}</td>
                  <td className="px-2 py-2 text-right num font-semibold text-[#16A34A]">{formatTL(li.aylik_fiyat)}</td>
                  <td className="px-2 py-2 text-right">
                    {form.line_items.length > 1 && (
                      <button onClick={() => removeLine(idx)} className="p-1.5 hover:bg-[#DC2626] hover:text-white"><Trash2 className="w-3 h-3" /></button>
                    )}
                  </td>
                </tr>
              ))}
              <tr className="bg-[#FAFAFA] border-t-2 border-[#0A0A0A] font-semibold">
                <td colSpan={7} className="px-2 py-3 text-right text-[11px] uppercase tracking-widest text-neutral-500">Toplam</td>
                <td className="px-2 py-3 text-right num">{formatTL(formTotals.tek_yon)}</td>
                <td className="px-2 py-3 text-right num">{formatTL(formTotals.gunluk)}</td>
                <td className="px-2 py-3 text-right num text-[#0A0A0A]" data-testid="offer-total-monthly">{formatTL(formTotals.aylik)}</td>
                <td></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Notes */}
      <div className="bg-white border border-[#E5E5E5] rounded-md p-6">
        <Field label="Notlar / Şartlar">
          <textarea rows={4} value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} className="input" placeholder="Örn: Fiyatlarımıza KDV dahil değildir. Yakıtta %10 ve üzeri artışlar fiyatlara yansır..." />
        </Field>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2 pb-24">
        <button
          data-testid="offer-save-btn"
          onClick={save}
          disabled={busy || !form.offer_no || !form.company_name}
          className="px-6 py-2.5 text-xs uppercase tracking-widest font-semibold bg-[#0A0A0A] text-white hover:bg-[#0020C2] transition-colors disabled:opacity-50"
        >
          {busy ? "Kaydediliyor..." : (editing ? "Kaydet" : "Teklifi Oluştur")}
        </button>
      </div>
    </div>
  );
}

function Field({ label, testid, children }) {
  return (
    <label className="block" data-testid={testid}>
      <div className="text-xs font-semibold uppercase tracking-widest text-neutral-600 mb-1.5">{label}</div>
      {children}
    </label>
  );
}

// ─── AI Suggest ────────────────────────────────────────────────────────────
function AISuggest({ aiRoutes, setAiRoutes, aiResult, runSuggest, applyAISuggestions }) {
  const updateRoute = (i, patch) => {
    const arr = [...aiRoutes]; arr[i] = { ...arr[i], ...patch }; setAiRoutes(arr);
  };
  const addRoute = () => setAiRoutes([...aiRoutes, { guzergah: "", arac_kapasitesi: "16+1", tek_yon_mesafe_km: 0, gunluk_sefer_sayisi: 2, calisma_gun_sayisi: 22, calisma_saatleri: "" }]);
  const removeRoute = (i) => setAiRoutes(aiRoutes.filter((_, idx) => idx !== i));

  return (
    <div className="space-y-6" data-testid="ai-suggest-panel">
      <div className="bg-white border border-[#E5E5E5] rounded-md p-6">
        <div className="flex items-center gap-3 mb-2">
          <Wand2 className="w-5 h-5 text-[#0020C2]" />
          <div>
            <div className="text-lg font-bold font-heading">Otomatik Teklif Oluşturucu</div>
            <div className="text-sm text-neutral-600 mt-1">Firma sizden teklif istediğinde, güzergah bilgilerini girin — sistem geçmiş tekliflerinizi analiz edip önerilen fiyatı otomatik üretir.</div>
          </div>
        </div>

        <div className="mt-6 space-y-3">
          {aiRoutes.map((r, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end p-3 border border-[#E5E5E5] bg-[#FAFAFA]" data-testid={`ai-route-${i}`}>
              <div className="md:col-span-3">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Güzergah</div>
                <input data-testid={`ai-route-guzergah-${i}`} value={r.guzergah} onChange={e => updateRoute(i, { guzergah: e.target.value })} placeholder="Etimesgut - Ostim" className="w-full px-2 py-2 bg-white border border-[#E5E5E5] text-xs" />
              </div>
              <div className="md:col-span-2">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Kapasite</div>
                <input value={r.arac_kapasitesi} onChange={e => updateRoute(i, { arac_kapasitesi: e.target.value })} className="w-full px-2 py-2 bg-white border border-[#E5E5E5] text-xs" />
              </div>
              <div className="md:col-span-2">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Mesafe (Km)</div>
                <input data-testid={`ai-route-km-${i}`} type="number" step="any" value={r.tek_yon_mesafe_km} onChange={e => updateRoute(i, { tek_yon_mesafe_km: e.target.value })} className="w-full px-2 py-2 bg-white border border-[#E5E5E5] text-xs num text-right" />
              </div>
              <div className="md:col-span-2">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Sefer/Gün</div>
                <input type="number" step="any" value={r.gunluk_sefer_sayisi} onChange={e => updateRoute(i, { gunluk_sefer_sayisi: e.target.value })} className="w-full px-2 py-2 bg-white border border-[#E5E5E5] text-xs num text-right" />
              </div>
              <div className="md:col-span-2">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500 mb-1">Gün Sayısı</div>
                <input type="number" step="any" value={r.calisma_gun_sayisi} onChange={e => updateRoute(i, { calisma_gun_sayisi: e.target.value })} className="w-full px-2 py-2 bg-white border border-[#E5E5E5] text-xs num text-right" />
              </div>
              <div className="md:col-span-1 flex justify-end">
                {aiRoutes.length > 1 && <button onClick={() => removeRoute(i)} className="p-2 hover:bg-[#DC2626] hover:text-white"><Trash2 className="w-3.5 h-3.5" /></button>}
              </div>
            </div>
          ))}
          <div className="flex justify-between pt-2">
            <button onClick={addRoute} className="flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-widest font-semibold border border-[#E5E5E5] hover:bg-[#F5F5F5]"><Plus className="w-3.5 h-3.5" /> Güzergah Ekle</button>
            <button data-testid="ai-run-btn" onClick={runSuggest} className="flex items-center gap-2 bg-[#0020C2] text-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] hover:bg-[#0A0A0A]"><Wand2 className="w-4 h-4" /> Otomatik Teklif Üret</button>
          </div>
        </div>
      </div>

      {aiResult && (
        <div className="bg-white border border-[#E5E5E5] rounded-md" data-testid="ai-result-panel">
          <div className="px-6 py-4 border-b border-[#E5E5E5] flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 font-semibold">Öneriler</div>
              <div className="text-lg font-bold font-heading mt-1">Geçmişten Öğrenilen Fiyatlar</div>
              <div className="text-xs text-neutral-500 mt-1">Örnek: <span className="num">{aiResult.historical_sample_size}</span> geçmiş kalem · Global ort: <span className="num font-semibold">{formatTL(aiResult.global_avg_tl_per_km)}</span> / km</div>
            </div>
            <button data-testid="ai-apply-btn" onClick={applyAISuggestions} className="flex items-center gap-2 bg-[#0A0A0A] text-white px-4 py-2 text-xs font-semibold uppercase tracking-[0.15em] hover:bg-[#0020C2]">
              Teklife Aktar →
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                <tr>
                  {["#","Güzergah","Km","Ort ₺/km","Tek Yön","Günlük","Aylık","Kaynak"].map((h, i) => (
                    <th key={h} className={`px-3 py-3 text-[10px] uppercase tracking-widest text-neutral-500 font-semibold ${i >= 2 && i <= 6 ? "text-right" : "text-left"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {aiResult.suggestions.map((s, i) => (
                  <tr key={i} className="border-b border-[#F5F5F5]">
                    <td className="px-3 py-3 text-neutral-400 num">{s.sira}</td>
                    <td className="px-3 py-3 font-medium">{s.guzergah || "-"} <span className="text-neutral-500 text-[10px] block">{s.arac_kapasitesi}</span></td>
                    <td className="px-3 py-3 text-right num">{formatNumber(s.tek_yon_mesafe_km, 1)}</td>
                    <td className="px-3 py-3 text-right num text-neutral-600">{formatTL(s.avg_tl_per_km)}</td>
                    <td className="px-3 py-3 text-right num font-semibold">{formatTL(s.tek_yon_fiyat)}</td>
                    <td className="px-3 py-3 text-right num">{formatTL(s.gunluk_fiyat)}</td>
                    <td className="px-3 py-3 text-right num font-semibold text-[#16A34A]">{formatTL(s.aylik_fiyat)}</td>
                    <td className="px-3 py-3">
                      <span className={`text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 ${
                        s.confidence_source === "route" ? "bg-[#16A34A] text-white" :
                        s.confidence_source === "similar" ? "bg-[#0020C2] text-white" :
                        s.confidence_source === "global" ? "bg-[#F59E0B] text-white" : "bg-neutral-200 text-neutral-700"
                      }`}>
                        {s.confidence_source === "route" ? "Aynı güzergah" : s.confidence_source === "similar" ? "Benzer km" : s.confidence_source === "global" ? "Genel ort." : "Veri yok"}
                      </span>
                      <span className="text-[10px] text-neutral-500 ml-2 num">n={s.sample_size}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Analytics ─────────────────────────────────────────────────────────────
function Analytics({ analytics }) {
  if (!analytics) return <div className="text-neutral-500 text-sm">Yükleniyor...</div>;
  return (
    <div className="space-y-6" data-testid="offers-analytics">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <KPICard label="Toplam Teklif" value={formatNumber(analytics.total_offers)} sub={`${analytics.total_line_items} güzergah`} icon={BarChart3} />
        <KPICard label="Ort. Aylık Ciro" value={formatTL(analytics.avg_offer_size)} sub="teklif başına" accent="info" icon={TrendingUp} />
        <KPICard label="Ort. Fiyat / Km" value={formatTL(analytics.global_avg_tl_per_km)} sub="tüm güzergahlar" accent="warning" />
        <KPICard label="Kazanma Oranı" value={`%${formatNumber(analytics.win_rate, 1)}`} sub="Kazanılan / Kazanılan+Kayıp" accent={analytics.win_rate >= 50 ? "success" : "error"} />
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-md p-5">
        <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 font-semibold">Mesafe Kategorisi Bazlı Fiyat</div>
        <div className="text-lg font-bold font-heading mt-1 mb-4">Km Bandına Göre Ortalama TL/km</div>
        {analytics.km_buckets.every(b => b.count === 0) ? (
          <div className="h-[220px] flex items-center justify-center text-sm text-neutral-500">Henüz veri yok</div>
        ) : (
          <div style={{ width: "100%", height: 260 }}>
            <ResponsiveContainer>
              <BarChart data={analytics.km_buckets}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E5E5" />
                <XAxis dataKey="bucket" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={(v) => new Intl.NumberFormat("tr-TR",{notation:"compact"}).format(v)} />
                <Tooltip formatter={(v, k) => k === "avg_tl_per_km" ? `${formatTL(v)}/km` : formatTL(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="avg_tl_per_km" name="Ort. ₺/km" fill="#0020C2" />
                <Bar dataKey="avg_monthly" name="Ort. Aylık ₺" fill="#F59E0B" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="bg-white border border-[#E5E5E5] rounded-md">
        <div className="px-5 py-4 border-b border-[#E5E5E5]">
          <div className="text-[11px] uppercase tracking-[0.15em] text-neutral-500 font-semibold">En Değerli Güzergahlar</div>
          <div className="text-lg font-bold font-heading mt-1">Toplam Aylık Ciro</div>
        </div>
        {analytics.top_routes.length === 0 ? (
          <div className="p-8 text-center text-sm text-neutral-500">Henüz güzergah yok</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                <tr>
                  {["#","Güzergah","Teklif Sayısı","Ort. ₺/km","Toplam Aylık"].map((h, i) => (
                    <th key={h} className={`px-4 py-3 text-[11px] uppercase tracking-widest text-neutral-500 font-semibold ${i >= 2 ? "text-right" : "text-left"}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {analytics.top_routes.map((r, i) => (
                  <tr key={i} className="border-b border-[#F5F5F5]">
                    <td className="px-4 py-3 text-neutral-400 num">{i + 1}</td>
                    <td className="px-4 py-3 font-medium">{r.route}</td>
                    <td className="px-4 py-3 text-right num">{r.count}</td>
                    <td className="px-4 py-3 text-right num text-neutral-600">{formatTL(r.avg_tl_per_km)}</td>
                    <td className="px-4 py-3 text-right num font-semibold text-[#16A34A]">{formatTL(r.total_monthly)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
